import React from "react";
import Images from "../../Images";
import {
  Mail,
  MapPinHouse,
  Facebook,
  Instagram,
  ExternalLink,
  Phone,
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Config from "../../Config";

const SocialLink = ({ href, icon: Icon, label }) => (
  <motion.a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ scale: 1.15, y: -3 }}
    whileTap={{ scale: 0.95 }}
    className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md text-gray-700 hover:text-[#FF6C6C] hover:shadow-lg transition-all duration-300"
    aria-label={label}
  >
    <Icon size={20} strokeWidth={1.5} />
  </motion.a>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FooterLink = ({ to, children, external }) => (
  <motion.li
    whileHover={{ scale: 1.05, x: 5 }}
    transition={{ type: "spring", stiffness: 400, damping: 10 }}
    className="text-gray-600 hover:text-[#FF6C6C] transition-colors duration-300"
  >
    <Link
      to={to}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="inline-flex items-center gap-1.5 text-sm font-medium"
    >
      {children}
      {external && <ExternalLink size={14} className="opacity-70" />}
    </Link>
  </motion.li>
);

const Footer = () => {
  return (
    <motion.footer
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="w-full bg-gradient-to-br from-[#FBDDD3] to-orange-50"
    >
      {/* Main Footer */}
      <div className="pt-16 pb-12 w-full">
        <div className="grid grid-cols-1 gap-10 px-6 mx-auto max-w-7xl lg:px-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: About */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            <motion.div whileHover={{ scale: 1.03 }} className="h-auto w-fit">
              <img
                src={Images.logo}
                alt="dealboxx logo"
                className="object-contain h-auto max-w-[140px]"
              />
            </motion.div>
            <p className="text-sm leading-relaxed text-gray-600">
              Dealboxx Vendor Portal is your gateway to reaching more customers
              and growing your business. List your deals, manage your services,
              and connect with potential customers efficiently.
            </p>
            <div className="flex space-x-3">
              <SocialLink
                href="https://facebook.com"
                icon={Facebook}
                label="Facebook"
              />
              <SocialLink href="https://twitter.com" icon={XIcon} label="X" />
              <SocialLink
                href="https://instagram.com"
                icon={Instagram}
                label="Instagram"
              />
            </div>
          </motion.div>

          {/* Column 2: Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-bold text-gray-800 after:content-[''] after:block after:w-10 after:h-1 after:bg-[#FF6C6C] after:mt-2">
              Quick Links
            </h3>
            <ul className="space-y-3">
              <FooterLink to="/support">Support Center</FooterLink>
              <FooterLink to={Config.consumerDomain} external>
                Find Best Deals
              </FooterLink>
              <FooterLink to="/support">Contact</FooterLink>
            </ul>
          </motion.div>

          {/* Column 3: Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-bold text-gray-800 after:content-[''] after:block after:w-10 after:h-1 after:bg-[#FF6C6C] after:mt-2">
              Services
            </h3>
            <ul className="space-y-3">
              <FooterLink to="/deals/create">Create Deal</FooterLink>
              <FooterLink to="/analytics">Analytics</FooterLink>
            </ul>
          </motion.div>

          {/* Column 4: Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-bold text-gray-800 after:content-[''] after:block after:w-10 after:h-1 after:bg-[#FF6C6C] after:mt-2">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3 items-start">
                <MapPinHouse
                  className="text-[#FF6C6C] mt-1 flex-shrink-0"
                  size={18}
                />
                <span className="text-sm text-gray-600">
                  71 Swaniker Street, Abelemkpe Accra.
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="text-[#FF6C6C] flex-shrink-0" size={18} />
                <a
                  href="tel:+233596921080"
                  className="text-sm text-gray-600 hover:text-[#FF6C6C] hover:underline transition-colors"
                >
                  +233 596 921 080
                </a>
              </li>
              <motion.button
                onClick={() =>
                  window.open(
                    "https://maps.app.goo.gl/nJtbVe99MLkiY6f4A",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#FF6C6C] hover:text-[#e55a5a] transition-colors duration-300 bg-white px-3 py-1.5 rounded-md shadow-sm hover:shadow-md border border-orange-200"
              >
                <MapPinHouse size={14} />
                Google Location
                <ExternalLink size={12} className="opacity-70" />
              </motion.button>
            </ul>
          </motion.div>
        </div>
      </div>

      {/* Copyright */}
      <div className="py-6 w-full border-t border-orange-200/70">
        <div className="flex flex-col justify-between items-center px-6 mx-auto max-w-7xl lg:px-8 md:flex-row">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Dealboxx. All rights reserved.
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <Link
              to="/privacy"
              className="text-xs text-gray-500 hover:text-[#FF6C6C]"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-xs text-gray-500 hover:text-[#FF6C6C]"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </motion.footer>
  );
};

export default Footer;
